import {
  CHAINS_INPUTS,
  CHALLENGE_INPUTS,
  FG_TOGGLE_BTN,
  PAT_INPUTS,
  PAT_TOGGLE_BTN,
  PENALTY_INPUTS,
  POSSESSION_INPUTS,
  PRIMARY_FLOW_INPUTS,
  REGULAR_PLAY_INPUTS,
  SAFTEY_BTN,
  TEventTypes,
  TOUCHDOWN_BTN,
  TPlayDesigns,
  TPlayResultTypes,
  TRefEventOutcomes,
  TWO_PT_TOGGLE_BTN,
  UNDER_REVIEW_INPUTS,
  YARDAGE_SELECTOR_BUTTONS,
} from '@/constants';
import { getFirstDownYardage } from '@/stores/util/playFlowHelper';
import {
  IConfirmationInput,
  IMachineContext,
  ITwoRowGenericSelectorInp,
  TMachineEvents,
  TMetaFuncInput,
  TOffDef,
  TPlayResults,
  TRefEvents,
  TRefEventsActions,
  TTeam,
  TWorkflowType,
} from '@/types';
import { isSaftey } from '@/workflow/utils';
import { cloneDeep, last } from 'lodash';

export function getCtxUpdate(inp: TMetaFuncInput) {
  return {
    currentPlay: {
      ...inp.context.currentPlay,
    },
  } as Partial<IMachineContext>;
}

function getSelectedRefOutcome(context: IMachineContext): number {
  return context.currentPlay?.refEventOutcomeId || TRefEventOutcomes.REVIEW;
}

function applyRefOutcomeToPreviousPlay(
  playFlowState: TMetaFuncInput['playFlowState'],
  generalState: TMetaFuncInput['generalState'],
  refOutcomeId: number
) {
  const lastValidPlay =
    playFlowState.plays && playFlowState.plays.length
      ? playFlowState.plays
          .slice()
          .reverse()
          .find((p) => !p.isPlayNullified)
      : null;

  if (!lastValidPlay) {
    return null;
  }

  lastValidPlay.refEventOutcomeId = refOutcomeId;
  playFlowState.queuePlayflowUpdate(generalState.currentGame.gameId, lastValidPlay, true);
  return lastValidPlay;
}

export function refHandleReiew(inp: TMetaFuncInput) {
  const { playFlowState, send, generalState } = inp;
  playFlowState.pushEvent(
    {
      eventTypeId: TEventTypes.UNDER_REVIEW,
      value: true,
    },
    generalState.currentGame.gameId
  );

  const ctxUpdate = getCtxUpdate(inp);
  ctxUpdate.currentPlay.refEventOutcomeId = TRefEventOutcomes.REVIEW;

  send(TRefEventsActions.ON_UNDER_REVIEW, {
    ctxUpdate,
    nextStateInput: {
      ...UNDER_REVIEW_INPUTS,
      backTo: TRefEventsActions.BACK,
    } as ITwoRowGenericSelectorInp,
  });
}

// handle chains ref event
export function refhandleChains(inp: TMetaFuncInput) {
  const { send } = inp;

  const ctxUpdate = getCtxUpdate(inp);
  ctxUpdate.currentPlay.refEventOutcomeId = TRefEventOutcomes.MEASUREMENT;

  send(TRefEventsActions.ON_CHAINS, {
    ctxUpdate,
    nextStateInput: {
      ...CHAINS_INPUTS,
    } as ITwoRowGenericSelectorInp,
  });
}

// handle challenge ref event
export function refHandleChallenge(inp: TMetaFuncInput) {
  const { generalState, send } = inp;

  const ctxUpdate = getCtxUpdate(inp);
  ctxUpdate.currentPlay.refEventOutcomeId = TRefEventOutcomes.CHALLENGE;

  send(TRefEventsActions.ON_CHALLENGE, {
    ctxUpdate,
    nextStateInput: {
      ...CHALLENGE_INPUTS(generalState.teams),
    },
  });
}

// handle penalty ref event
export function refHandlePenalty(inp: TMetaFuncInput) {
  const { playFlowState, send, generalState } = inp;

  const ctxUpdate = getCtxUpdate(inp);
  ctxUpdate.currentPlay.refEventOutcomeId = TRefEventOutcomes.PENALTY;

  send(TRefEventsActions.PENALTY_SELECTED, {
    ctxUpdate,
    nextStateInput: {
      ...PENALTY_INPUTS(
        playFlowState.currentTeamRoles.offense,
        generalState.teams[playFlowState.currentTeamRoles.offense].logoUrl,
        playFlowState.currentTeamRoles.defense,
        generalState.teams[playFlowState.currentTeamRoles.defense].logoUrl
      ),
      backTo: TRefEventsActions.BACK,
    },
  });
}

export function refHandleOutcome(inp: TMetaFuncInput) {
  const { payload, context, send } = inp;

  switch (payload.value) {
    case TRefEvents.REVIEW:
      return refHandleReiew(inp);
    case TRefEvents.CHAINS:
      return refhandleChains(inp);
    case TRefEvents.CHALLENGE:
      return refHandleChallenge(inp);
    case TRefEvents.PENALTY:
      return refHandlePenalty(inp);
    case TRefEvents.NO_PLAY:
    default:
      return send(context.inputForNextComponent.backTo, {});
  }
}

export function handleBackFromRefSelect(inp: TMetaFuncInput) {
  const { send, context } = inp;
  const ctxUpdate = getCtxUpdate(inp);
  send(context.inputForNextComponent.backTo, { ctxUpdate });
}

export function handleUnderReview(inp: TMetaFuncInput) {
  const { generalState, playFlowState, send, payload, context } = inp;
  const refOutcomeId = getSelectedRefOutcome(context);
  const lastValidPlay =
    playFlowState.plays && playFlowState.plays.length
      ? playFlowState.plays
          .slice()
          .reverse()
          .find((p) => !p.isPlayNullified)
      : null;

  if (payload.value === TPlayResults.OVERTURNED) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.OVERTURNED,
        value: true,
      },
      generalState.currentGame.gameId
    );

    // Revert to last valid play state (do not nullify last play)
    playFlowState.revertToLastValidPlayState();

    // Enter Edit Mode for the play that was reviewed
    if (lastValidPlay) {
      lastValidPlay.refEventOutcomeId = refOutcomeId;
      playFlowState.initEditPlay(lastValidPlay);
    }

    if (
      playFlowState.lastPlay.playDesignId === TPlayDesigns.EXTRA_PT ||
      playFlowState.lastPlay.playDesignId === TPlayDesigns.TWO_PT_CONVERSION
    ) {
      send(TRefEventsActions.ON_OVERTURNED_PAT, {
        ctxUpdate: {
          currentWorkflow: TWorkflowType.PAT,
        },
        nextStateInput: PAT_INPUTS,
      });
    } else {
      send(TRefEventsActions.ON_OVERTURNED, {
        ctxUpdate: {},
        nextStateInput: PRIMARY_FLOW_INPUTS,
      });
    }
  }

  if (!context.currentWorkflow) {
    // Review on Idle screen
    applyRefOutcomeToPreviousPlay(playFlowState, generalState, refOutcomeId);
    send(TRefEventsActions.ON_UNDER_REVIEW_SELECTED, {
      ctxUpdate: {},
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  } else if (context.currentWorkflow === TWorkflowType.PAT) {
    // Review/Challenge on PAT applies to the previous valid play.
    applyRefOutcomeToPreviousPlay(playFlowState, generalState, refOutcomeId);
    send(TRefEventsActions.ON_UNDER_REVIEW_SELECTED, {
      ctxUpdate: {},
      nextStateInput: PAT_INPUTS,
    });
  } else if (!context.yardageToggleData && lastValidPlay) {
    // Review on Play Design screen
    applyRefOutcomeToPreviousPlay(playFlowState, generalState, refOutcomeId);

    send(TRefEventsActions.BACK_TO_IDLE, {
      ctxUpdate: {},
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  } else {
    // Review on Confirmation screen
    send(TRefEventsActions.ON_UNDER_REVIEW_SELECTED, {
      ctxUpdate: {},
      nextStateInput: {
        buttons: YARDAGE_SELECTOR_BUTTONS(),
        sliderLabels: {
          left: generalState.teams[playFlowState.teams.left].abbreviation,
          right: generalState.teams[playFlowState.teams.right].abbreviation,
        },
        backTo: TRefEventsActions.BACK_TO_UNDER_REVIEW,
      },
    });
  }
}

// handle chains event
export function handleChains(inp: TMetaFuncInput) {
  const { generalState, playFlowState, send, payload, context } = inp;

  let nextStateInput: any = {
    buttons: YARDAGE_SELECTOR_BUTTONS(),
    sliderLabels: {
      left: generalState.teams[playFlowState.teams.left].abbreviation,
      right: generalState.teams[playFlowState.teams.right].abbreviation,
    },
    backTo: TRefEventsActions.BACK_TO_CHAIN,
  };

  const isFirstDown = payload.value === TPlayResults.FIRST_DOWN;

  if (isFirstDown) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.FIRST_DOWN,
        value: true,
      },
      generalState.currentGame.gameId
    );
  }

  if (
    !!context.currentWorkflow &&
    (last(context.prevStateInputs).backTo?.includes('CONFIRMATION') || isFirstDown)
  ) {
    send(TRefEventsActions.COMPLETE_PLAY, {
      ctxUpdate: {
        currentWorkflow: null,
        currentPlay: {
          ...context.currentPlay,
          isFirstDown,
        },
      },
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  } else if (!context.currentWorkflow) {
    nextStateInput = PRIMARY_FLOW_INPUTS;
    if (isFirstDown) {
      playFlowState.updateLastPlay(
        {
          down: 1,
          distance: getFirstDownYardage(
            context.currentPlay.postPlayYardLine || playFlowState.yardLine.number,
            context.currentPlay.postPlaySide || playFlowState.yardLine.side,
            playFlowState.driveDirection
          ),
        },
        generalState.currentGame.gameId
      );
    }
  }

  send(TRefEventsActions.ON_CHAINS_SELECTED, {
    ctxUpdate: {
      currentPlay: {
        ...context.currentPlay,
        isFirstDown,
      },
    },
    nextStateInput,
  });
}

// handle challenge event
export function handleChallenge(inp: TMetaFuncInput) {
  const { send } = inp;
  send(TRefEventsActions.ON_CHALLENGE_SELECTED, {
    nextStateInput: {
      ...UNDER_REVIEW_INPUTS,
      backTo: TRefEventsActions.BACK_TO_CHALLANGE,
    } as ITwoRowGenericSelectorInp,
  });
}

// handle penalty event
export function handlePenalty(inp: TMetaFuncInput) {
  const { send, context, payload, generalState, playFlowState } = inp;
  const ctxUpdate: Partial<IMachineContext> = {
    currentPlay: {
      ...context.currentPlay,
      resultType: context.currentPlay?.resultType || [],
      options: {
        ...context.currentPlay.options,
      },
    },
  };

  if (!ctxUpdate.currentPlay.playDesignId) {
    ctxUpdate.currentPlay.playDesignId = TPlayDesigns.NO_PLAY;
  }

  switch (payload.value) {
    case TTeam.HOME:
      ctxUpdate.currentPlay.resultType.push(TPlayResultTypes.PENALTY_HOME);
      ctxUpdate.currentPlay.penalty =
        playFlowState.currentTeamRoles.offense === TTeam.HOME ? TOffDef.OFFENSE : TOffDef.DEFENSE;
      break;
    case TTeam.AWAY:
      ctxUpdate.currentPlay.resultType.push(TPlayResultTypes.PENALTY_AWAY);
      ctxUpdate.currentPlay.penalty =
        playFlowState.currentTeamRoles.offense === TTeam.AWAY ? TOffDef.OFFENSE : TOffDef.DEFENSE;
      break;
    case TPlayResults.GROUNDING:
      ctxUpdate.currentPlay.resultType.push(
        playFlowState.currentTeamRoles.offense === TTeam.AWAY
          ? TPlayResultTypes.PENALTY_AWAY
          : TPlayResultTypes.PENALTY_HOME
      );
      ctxUpdate.currentPlay.penalty = TOffDef.OFFENSE;
      ctxUpdate.currentPlay.options = {
        ...ctxUpdate.currentPlay.options,
        isIntenionalGrounding: true,
      };
      ctxUpdate.currentPlay.playDesignId = TPlayDesigns.PASS;
      break;
    case TOffDef.BOTH:
      ctxUpdate.currentPlay.resultType.push(TPlayResultTypes.PENALTY_BOTH);
      ctxUpdate.currentPlay.penalty = TOffDef.BOTH;
      break;
    case TPlayResults.DECLINED:
    default:
  }

  /*
  determine resulting possession if:
  - any penalty on a kickoff
  - defensive penalty on a punt or FG
  - any penalty on a turnover
  - pre-snap punt penalty (no play design set yet): routes possession → down → yardline
    in that order, avoiding a duplicate yardline selection via handleGenericConfirmation
  */
  if (
    context.currentPlay.playDesignId === TPlayDesigns.KICKOFF ||
    (ctxUpdate.currentPlay.penalty === TOffDef.DEFENSE &&
      [TPlayDesigns.PUNT, TPlayDesigns.FG].includes(context.currentPlay.playDesignId)) ||
    context.currentPlay.options?.isTov ||
    (context.currentWorkflow === TWorkflowType.PUNT && !context.currentPlay.playDesignId)
  ) {
    return send(TRefEventsActions.ON_POSSESSION_SELECT, {
      ctxUpdate,
      nextStateInput: {
        ...POSSESSION_INPUTS(generalState.teams),
        backTo: TRefEventsActions.BACK_TO_PENALTY,
        title: 'Select Possession',
      },
    });
  }

  // if anything other than decline or offsetting punt penalty, go to post penalty yardage
  if (
    payload.value !== TPlayResults.DECLINED &&
    !(payload.value === TOffDef.BOTH && context.currentPlay.playDesignId === TPlayDesigns.PUNT)
  ) {
    if (context.currentWorkflow === TWorkflowType.PAT && !context.currentPlay.options?.isKickoffSnapped) {
      ctxUpdate.currentPlay.options.isPreSnapPATPenalty = true;
    } else if (
      context.currentPlay.playDesignId === TPlayDesigns.KICKOFF &&
      context.currentPlay.options?.isKickoffSnapped
    ) {
      ctxUpdate.currentPlay.options.isPenaltyDuringReturn = true;
    } else if (context.currentWorkflow === TWorkflowType.REGULAR_PLAY) {
      ctxUpdate.currentPlay = {
        ...ctxUpdate.currentPlay,
        penaltyYardLine: context.currentPlay.postPlayYardLine,
        penaltySide: context.currentPlay.postPlaySide,
      };
      // On 4th down with a non-turnover penalty the offense always retains possession
      // (defensive penalty → auto first down; offensive penalty → replay 4th down).
      // Setting this here prevents handleGenericConfirmation from triggering an
      // additional SELECT_POSSESSION → SELECT_YARDAGE round-trip on the confirmation screen.
      if (playFlowState.down === 4) {
        ctxUpdate.currentPlay.options = {
          ...ctxUpdate.currentPlay.options,
          isPossessionRetained: true,
        };
      }
    }

    return send(TRefEventsActions.ON_PENALTY_SELECTED, {
      ctxUpdate,
      nextStateInput: {
        buttons: YARDAGE_SELECTOR_BUTTONS(),
        sliderLabels: {
          left: generalState.teams[playFlowState.teams.left].abbreviation,
          right: generalState.teams[playFlowState.teams.right].abbreviation,
        },
        backTo: TRefEventsActions.BACK_TO_PENALTY,
        title: 'Post-Penalty Yardline',
      },
    });
  }

  if (payload.value === TOffDef.BOTH && context.currentPlay.playDesignId === TPlayDesigns.PUNT) {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.PENALTY_BOTH];
    ctxUpdate.currentPlay.postPlaySide = playFlowState.yardLine.side;
    ctxUpdate.currentPlay.postPlayYardLine = playFlowState.yardLine.number;
  }

  // PENALTY DECLINED HANDLING BELOW
  if (ctxUpdate.currentPlay?.resultType.length) {
    ctxUpdate.currentPlay.resultType = ctxUpdate.currentPlay.resultType.filter(
      (res) =>
        ![
          TPlayResultTypes.PENALTY_AWAY,
          TPlayResultTypes.PENALTY_HOME,
          TPlayResultTypes.PENALTY_BOTH,
        ].includes(res)
    );
  }

  // Keep the ball spot when a declined penalty is logged without a yardage step.
  if (
    ctxUpdate.currentPlay.postPlayYardLine === undefined ||
    ctxUpdate.currentPlay.postPlayYardLine === null
  ) {
    ctxUpdate.currentPlay.postPlayYardLine =
      context.currentPlay.postPlayYardLine ?? playFlowState.yardLine?.number;
  }

  if (ctxUpdate.currentPlay.postPlaySide === undefined || ctxUpdate.currentPlay.postPlaySide === null) {
    ctxUpdate.currentPlay.postPlaySide = context.currentPlay.postPlaySide ?? playFlowState.yardLine?.side;
  }

  ctxUpdate.currentPlay.penalty = null;

  // clear flags from an accepted-then-reversed penalty selection so a declined penalty doesn't redo the play
  if (ctxUpdate.currentPlay.options?.isPreSnapPATPenalty) {
    ctxUpdate.currentPlay.options.isPreSnapPATPenalty = false;
  }
  if (ctxUpdate.currentPlay.options?.isPenaltyDuringReturn) {
    ctxUpdate.currentPlay.options.isPenaltyDuringReturn = false;
  }

  // go back to play input if result type not present
  if (!ctxUpdate.currentPlay?.resultType?.length) {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.PENALTY_DECLINED];
    return send(TMachineEvents.PENALTY_DECLINED, {
      ctxUpdate: {
        ...ctxUpdate,
      },
      nextStateInput: context.currentWorkflow === TWorkflowType.PAT ? PAT_INPUTS : PRIMARY_FLOW_INPUTS,
    });
  }

  // send straight to confirmation if it has a result type already
  // need to determine if bottom toggle btn is needed
  const nextStateInput: IConfirmationInput = {
    backTo: TMachineEvents.BACK_TO_PENALTY,
  };

  if (
    context.currentPlay.postPlayYardLine === 0 &&
    isSaftey(inp, { yardLine: context.currentPlay.postPlayYardLine, side: context.currentPlay.postPlaySide })
  ) {
    nextStateInput.toggleButton = cloneDeep(SAFTEY_BTN);
  } else if (context.currentPlay.postPlayYardLine === 0) {
    nextStateInput.toggleButton = cloneDeep(TOUCHDOWN_BTN);
  } else if (context.currentPlay.playDesignId === TPlayDesigns.EXTRA_PT) {
    nextStateInput.toggleButton = cloneDeep(PAT_TOGGLE_BTN);
    nextStateInput.toggleButton.toggleInitialValue =
      last(ctxUpdate.currentPlay.resultType) === TPlayResultTypes.EXTRA_PT;
  } else if (context.currentPlay.playDesignId === TPlayDesigns.TWO_PT_CONVERSION) {
    nextStateInput.toggleButton = cloneDeep(TWO_PT_TOGGLE_BTN);
    nextStateInput.toggleButton.toggleInitialValue =
      last(ctxUpdate.currentPlay.resultType) === TPlayResultTypes.CONVERSION_2PT;
  } else if (context.currentPlay.playDesignId === TPlayDesigns.FG) {
    nextStateInput.toggleButton = FG_TOGGLE_BTN(
      last(ctxUpdate.currentPlay.resultType) === TPlayResultTypes.FG
    );
  }

  return send(TMachineEvents.PENALTY_DECLINED_TO_CONFIRM, {
    ctxUpdate,
    nextStateInput,
  });
}
